import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import { nombresRepetidos, claveDeNombre, useRecursos, type Recurso } from './api';
import { Alertas } from './Alertas';
import { FormularioDeRecurso } from './FormularioDeRecurso';
import { RevisionDelMaestro } from './Maestro';
import { BuscadorDeEmpleado } from './piezas';

/**
 * Recursos — la rejilla de bases de datos. Módulo 15, la puerta de la pantalla.
 *
 * ## Lo que cambia
 *
 * - **Cada zona tiene su dirección**: `#recursos-privados/recurso/<id>`,
 *   `#recursos-privados/expediente/<id>` y la sub-ruta `recoleccion`, que ya
 *   existía. El vanilla repintaba el contenedor guardando el recurso abierto en
 *   una variable de módulo: el Atrás del navegador salía de la pantalla y no se
 *   podía enlazar una base de datos concreta.
 * - **Las tarjetas son enlaces.** Eran `<button>` con `onclick`.
 * - Los tres diálogos de arriba —expedientes, alertas y revisión del maestro—
 *   cuentan lo suyo en su archivo.
 */
export function Recursos() {
  const consulta = useRecursos();
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<number | null>(null);
  const [abriendoExpediente, setAbriendoExpediente] = useState(false);
  const [viendoAlertas, setViendoAlertas] = useState(false);
  const [revisandoMaestro, setRevisandoMaestro] = useState(false);

  const recursos = consulta.data ?? [];
  const bases = recursos.filter((r) => !r.is_expediente || r.is_system);
  const subdominios = recursos.filter((r) => r.is_expediente && !r.is_system);
  // Se mira la lista COMPLETA, no cada sección: los dos gemelos pueden caer en
  // secciones distintas (uno de sistema y otro no) y entonces ninguna de las dos
  // vería el choque por su cuenta.
  const repetidos = nombresRepetidos(recursos);

  return (
    <>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-hdd-network" aria-hidden="true" /> Recursos y Datos
          </h1>
          <p className="mt-1 text-3 text-muted">
            Bases de datos internas configurables, con historial y auditoría.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setAbriendoExpediente(true)}>
            <i className="bi bi-folder2-open" aria-hidden="true" /> Expedientes
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setViendoAlertas(true)}>
            <i className="bi bi-bell" aria-hidden="true" /> Alertas
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setRevisandoMaestro(true)}>
            <i className="bi bi-arrow-repeat" aria-hidden="true" /> Revisar el maestro
          </Button>
          <Button size="sm" onClick={() => setCreando(true)}>
            <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nuevo recurso
          </Button>
        </div>
      </header>

      {consulta.isPending ? (
        <SkeletonTarjetas n={6} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las bases de datos"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !recursos.length ? (
        <EstadoVacio
          icono="bi-table"
          titulo="Sin bases de datos"
          descripcion="Crea la primera: proveedores, convenios, contactos…"
          accion={{ texto: 'Nuevo recurso', onClick: () => setCreando(true) }}
        />
      ) : (
        <>
          <Seccion
            titulo="Bases de datos"
            ayuda="Información que no cuelga de un colaborador concreto."
            recursos={bases}
            repetidos={repetidos}
            onEditar={setEditando}
          />
          <Seccion
            titulo="Sub-dominios del expediente"
            ayuda="Cada registro pertenece a un colaborador. Se consultan y se agregan desde Expedientes."
            recursos={subdominios}
            repetidos={repetidos}
            onEditar={setEditando}
          />
        </>
      )}

      {(creando || editando != null) && (
        <FormularioDeRecurso
          id={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
        />
      )}
      {viendoAlertas && <Alertas onCerrar={() => setViendoAlertas(false)} />}
      {revisandoMaestro && <RevisionDelMaestro onCerrar={() => setRevisandoMaestro(false)} />}
      {abriendoExpediente && (
        <SelectorDeExpediente onCerrar={() => setAbriendoExpediente(false)} />
      )}
    </>
  );
}

function Seccion({
  titulo, ayuda, recursos, repetidos, onEditar,
}: {
  titulo: string;
  ayuda: string;
  recursos: Recurso[];
  /** Claves de nombre que aparecen más de una vez en toda la pantalla. */
  repetidos: Set<string>;
  onEditar: (id: number) => void;
}) {
  if (!recursos.length) return null;
  return (
    <section className="mb-6">
      <h2 className="text-3 font-bold text-brand-ink">{titulo}</h2>
      <p className="mb-3 text-2 text-muted">{ayuda}</p>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {recursos.map((r) => (
          <li key={r.id} className="flex">
            <TarjetaDeRecurso
              recurso={r}
              repetido={repetidos.has(claveDeNombre(r.name))}
              onEditar={() => onEditar(r.id)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Una tarjeta de la rejilla.
 *
 * ## Por qué existe `repetido`
 *
 * El 2026-09-25 había DOS recursos llamados «Base de Datos Empleados»: el
 * maestro de sistema y uno creado a mano, sin registros. En la rejilla eran dos
 * tarjetas idénticas —mismo nombre, mismo icono, mismo color— y la única forma
 * de saber cuál era cuál era abrir las dos.
 *
 * El servidor ya no deja crear el segundo, pero los que existen siguen ahí, así
 * que la tarjeta tiene que poder decirlo. Lleva el aviso Y el número del
 * recurso: el aviso explica qué pasa y el número es lo que de verdad las
 * distingue.
 */
function TarjetaDeRecurso({ recurso, repetido, onEditar }: {
  recurso: Recurso;
  repetido: boolean;
  onEditar: () => void;
}) {
  return (
    <Card interactiva className="flex w-full flex-col overflow-hidden">
      <a
        href={`#recursos-privados/recurso/${recurso.id}`}
        className="flex flex-1 flex-col focus-visible:focus-anillo"
      >
        {/* El color lo elige quien crea el recurso: es dato, no tema. */}
        <span
          className="flex h-20 items-center justify-center text-8 text-on-brand"
          style={{ background: recurso.color }}
          aria-hidden="true"
        >
          <Icono nombre={recurso.icon} respaldo="bi-database" />
        </span>
        <span className="flex flex-1 flex-col gap-1 p-3">
          <strong className="text-3 font-bold text-brand-ink">{recurso.name}</strong>
          {recurso.description && (
            <span className="line-clamp-2 text-2 text-muted">{recurso.description}</span>
          )}
          <span className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-2 text-muted">
            <span>
              <i className="bi bi-table" aria-hidden="true" /> {recurso.entry_count}{' '}
              {recurso.entry_count === 1 ? 'registro' : 'registros'}
            </span>
            <span>{recurso.field_count} {recurso.field_count === 1 ? 'columna' : 'columnas'}</span>
            {recurso.is_system && (
              <Badge tone="info">
                <i className="bi bi-shield-lock" aria-hidden="true" /> Sistema
              </Badge>
            )}
            {repetido && (
              <Badge tone="warn">
                <i className="bi bi-exclamation-triangle" aria-hidden="true" /> Nombre
                repetido · n.º {recurso.id}
              </Badge>
            )}
          </span>
        </span>
      </a>
      <div className="border-t border-line px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onEditar}>
          <i className="bi bi-sliders" aria-hidden="true" /> Columnas
        </Button>
      </div>
    </Card>
  );
}

/** Abrir el expediente de alguien. Es un diálogo y no una pantalla porque lo
 *  único que hace es elegir a la persona; el expediente tiene su dirección. */
function SelectorDeExpediente({ onCerrar }: { onCerrar: () => void }) {
  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Abrir el expediente de un colaborador"
      descripcion="Reúne sus documentos, formación, incapacidades, vacaciones y más."
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      <div className="pb-2">
        <BuscadorDeEmpleado
          id="exp-empleado"
          etiqueta="Buscar colaborador por su nombre"
          alElegir={(e) => { location.hash = `#recursos-privados/expediente/${e.id}`; }}
        />
      </div>
    </Dialog>
  );
}
